import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon, ExternalLinkIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useDateWindow } from '@/components/date-range-picker'
import { DetailList, useListExpansion } from '@/components/detail-list'
import { EmptyState } from '@/components/empty-state'
import { MetricCards } from '@/components/metric-cards'
import { Panel } from '@/components/panel'
import { RankedList } from '@/components/ranked-list'
import { Button } from '@/components/ui/button'
import { formatDate, formatRelative, shortenUrl } from '@/lib/format'
import {
  DETAIL_PAGE_SIZE,
  type PageDetail,
  type PageInspection,
  type PageSitemapFacts,
  useInspectPage,
  usePageDetail,
  usePageInspection,
  usePageKeywords,
  usePageRelated,
} from '@/lib/site-data'
import { cn } from '@/lib/utils'
import { BACK_LINK, DetailHeader, MetricsSkeleton, ShareOfSite, useSiteScope } from '../-section'

export const Route = createFileRoute('/_app/$org/$host/pages/$pageId')({
  component: PageDetailPage,
})

/**
 * One url, from both feeds at once: what search did with it, and what the site
 * says about it. They are on the same page because the interesting cases are
 * the disagreements — a page nothing links to that still ranks, a page the
 * sitemap dropped that search has not noticed yet.
 */
function PageDetailPage() {
  const { org, host, range, site } = useSiteScope()
  const { pageId } = Route.useParams()
  const { data, isPending, error } = usePageDetail(site, range, Number(pageId))
  useDateWindow(data?.range)

  return (
    <>
      <DetailHeader
        back={
          <Link
            to="/$org/$host/pages"
            params={{ org, host }}
            search={{ range }}
            className={BACK_LINK}
          >
            <ArrowLeftIcon className="size-3" aria-hidden />
            Pages
          </Link>
        }
        title={data ? shortenUrl(data.page.url, host) : '…'}
        actions={
          data && (
            <a
              href={data.page.url}
              target="_blank"
              rel="noreferrer"
              className="mono-label inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground"
            >
              Open
              <ExternalLinkIcon className="size-3" aria-hidden />
            </a>
          )
        }
      />

      {error ? (
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            title="Page not found"
            description={`No url with that id belongs to ${host}. It may belong to another site.`}
            action={
              <Button asChild size="sm" variant="outline">
                <Link to="/$org/$host/pages" params={{ org, host }} search={{ range }}>
                  Back to pages
                </Link>
              </Button>
            }
          />
        </div>
      ) : isPending || !data ? (
        <MetricsSkeleton />
      ) : (
        <PageBody data={data} org={org} host={host} pageId={Number(pageId)} />
      )}
    </>
  )
}

/**
 * Unlike the report it came from, this page does not gate on a connected
 * source: the sitemap crawl needs nothing but the domain, so the two panels
 * underneath still answer for a url search has never shown.
 */
function PageBody({
  data,
  org,
  host,
  pageId,
}: {
  data: PageDetail
  org: string
  host: string
  pageId: number
}) {
  const { range, site } = useSiteScope()
  const keywords = useListExpansion(range, DETAIL_PAGE_SIZE)
  const related = useListExpansion(range, DETAIL_PAGE_SIZE)

  const keywordsPage = usePageKeywords(site, range, pageId, keywords.offset, keywords.expanded)
  const relatedPage = usePageRelated(site, range, pageId, related.offset, related.expanded)

  const keywordRows = (keywords.expanded ? keywordsPage.data?.rows : null) ?? data.keywords.rows
  const relatedRows = (related.expanded ? relatedPage.data?.rows : null) ?? data.related.rows

  return (
    <div className="space-y-4">
      {data.state === 'not-connected' ? (
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            title="No search data yet"
            description={`${host} is being read for its own sitemaps already. Connect the account that owns it and this page's search history fills in alongside.`}
            action={
              <Button asChild size="sm">
                <Link to="/$org/settings/connections" params={{ org }}>
                  Connect a source
                </Link>
              </Button>
            }
          />
        </div>
      ) : (
        <div className="space-y-3">
          <MetricCards totals={data.totals} series={data.series} />
          <ShareOfSite part={data.totals.clicks} whole={data.topline.clicks} />
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <DetailList
          title="Keywords"
          total={data.keywords.total}
          expansion={keywords}
          isFetching={keywordsPage.isFetching}
        >
          <RankedList
            dimension="Keyword"
            rows={keywordRows.map((row) => ({
              key: String(row.queryId),
              label: row.query,
              mono: true,
              metrics: row,
            }))}
            empty="No keyword-level detail for this page in this window. Rare searches are never attributed to a term, and pairing a page with a keyword is the last report to fill in."
            renderLabel={(row) => (
              <Link
                to="/$org/$host/keywords/$queryId"
                params={{ org, host, queryId: row.key }}
                search={{ range }}
                className="underline-offset-2 hover:text-plum hover:underline"
              >
                {row.label}
              </Link>
            )}
          />
        </DetailList>

        {/* Two pages answering one keyword is the usual explanation for a page
            that will not climb, so the shared count is the column that matters. */}
        <DetailList
          title="Pages sharing these keywords"
          total={data.related.total}
          expansion={related}
          isFetching={relatedPage.isFetching}
        >
          <RankedList
            dimension="Page"
            metaLabel="Keywords"
            rows={relatedRows.map((row) => ({
              key: String(row.pageId),
              label: shortenUrl(row.url, host),
              mono: true,
              meta: row.shared,
              metrics: row,
            }))}
            empty="Nothing else on the site ranks for this page's keywords in this window."
            renderLabel={(row) => (
              <Link
                to="/$org/$host/pages/$pageId"
                params={{ org, host, pageId: row.key }}
                search={{ range }}
                className="underline-offset-2 hover:text-plum hover:underline"
              >
                {row.label}
              </Link>
            )}
          />
        </DetailList>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Sitemap">
          <SitemapFacts facts={data.sitemap} page={data.page} />
        </Panel>

        <Panel title="Indexing">
          <PageIndexing pageId={data.page.id} />
        </Panel>
      </div>
    </div>
  )
}

/**
 * What the site itself says about this url. Never phrased as indexed or not:
 * that is Google's answer, it costs quota, and it lives in the panel beside
 * this one.
 */
function SitemapFacts({ facts, page }: { facts: PageSitemapFacts; page: PageDetail['page'] }) {
  return (
    <dl className="grid gap-x-6 gap-y-1.5 text-[12px] sm:grid-cols-[auto_1fr]">
      <Fact label="Declared">
        {facts.declared ? (
          `listed in ${facts.documents.length} ${facts.documents.length === 1 ? 'sitemap' : 'sitemaps'}`
        ) : facts.everDeclared ? (
          <span className="text-warning">
            dropped{facts.removedAt ? ` ${formatRelative(facts.removedAt)}` : ''}
          </span>
        ) : (
          'in no sitemap'
        )}
      </Fact>

      {facts.lastmod && <Fact label="Changed">{formatDate(facts.lastmod)}</Fact>}

      {facts.priority !== null && (
        <Fact label="Priority">
          {facts.priority.toFixed(1)}
          {facts.changefreq ? ` · ${facts.changefreq}` : ''}
        </Fact>
      )}

      {facts.blockedByRobots && (
        <Fact label="Robots">
          <span className="text-warning">disallowed by robots.txt</span>
        </Fact>
      )}

      <Fact label="First seen">
        {formatDate(page.firstSeenAt)}
        <span className="text-muted-foreground">
          {page.firstSeenVia === 'sitemap' ? ' in a sitemap' : ' in search'}
        </span>
      </Fact>
    </dl>
  )
}

/**
 * Whether Google can see this page, asked one page at a time.
 *
 * It is a button rather than a nightly job because the answer costs a unit of a
 * quota the whole property shares, and because a page with impressions has
 * already told us it is indexed — the question is only worth asking of the one
 * page someone is looking at and cannot explain.
 */
function PageIndexing({ pageId }: { pageId: number }) {
  const { site } = useSiteScope()
  // The stamp the stored result carried when the user asked for a fresh one.
  // The job answers on a queue, so "has it come back" is "is this different",
  // with '' standing in for a page that has never been inspected at all.
  const [awaiting, setAwaiting] = useState<string | null>(null)
  const { data, isPending } = usePageInspection(site, pageId, {
    enabled: true,
    poll: awaiting !== null,
  })
  const inspect = useInspectPage(site)
  const inspection = data?.inspection ?? null
  const stamp = inspection?.inspectedAt ?? ''

  useEffect(() => {
    if (awaiting !== null && stamp !== awaiting) setAwaiting(null)
  }, [awaiting, stamp])

  // Quota can be exhausted and the connection can be broken, in which case the
  // job returns having written nothing and nothing would ever clear the wait.
  useEffect(() => {
    if (awaiting === null) return
    const timer = setTimeout(() => setAwaiting(null), 90_000)
    return () => clearTimeout(timer)
  }, [awaiting])

  const waiting = awaiting !== null || inspect.isPending

  return (
    <div className="space-y-2">
      <div className="mono-label flex items-center gap-3 text-muted-foreground">
        {inspection && !waiting && <span>checked {formatRelative(inspection.inspectedAt)}</span>}
        <button
          type="button"
          disabled={waiting}
          onClick={() => {
            setAwaiting(stamp)
            inspect.mutate(pageId)
          }}
          className="ml-auto text-foreground underline-offset-2 transition-colors hover:text-plum hover:underline disabled:text-muted-foreground disabled:no-underline"
        >
          {waiting ? 'checking…' : inspection ? 'check again' : 'check indexing'}
        </button>
      </div>

      {inspect.isError && (
        <p className="text-[12px] text-warning">
          {inspect.error instanceof Error ? inspect.error.message : 'Could not request a check.'}
        </p>
      )}

      {isPending ? (
        <p className="font-mono text-[12px] text-muted-foreground">loading…</p>
      ) : inspection ? (
        <InspectionFacts inspection={inspection} />
      ) : (
        <p className="max-w-[70ch] text-[12px] text-muted-foreground">
          {waiting
            ? 'Checking. This takes a few seconds, and the result stays here once it arrives.'
            : 'Nothing checked yet. A check asks whether this page is indexed, when it was last crawled, and which URL was treated as the canonical one.'}
        </p>
      )}
    </div>
  )
}

/**
 * The canonical pair is given its own line because a mismatch is the finding —
 * the page is indexed, and the impressions are being credited elsewhere.
 */
function InspectionFacts({ inspection }: { inspection: PageInspection }) {
  const indexed = inspection.coverageState?.toLowerCase().includes('indexed')
  const canonicalMismatch =
    inspection.googleCanonical &&
    inspection.userCanonical &&
    inspection.googleCanonical !== inspection.userCanonical

  return (
    <dl className="grid gap-x-6 gap-y-1.5 text-[12px] sm:grid-cols-[auto_1fr]">
      <Fact label="State">
        <span className="inline-flex items-center gap-2">
          <span
            className={cn(
              'size-1.5 shrink-0 rounded-full',
              indexed ? 'bg-success' : 'bg-muted-foreground/40',
            )}
            aria-hidden
          />
          {inspection.coverageState ?? 'unknown'}
        </span>
      </Fact>

      {inspection.lastCrawlTime && (
        <Fact label="Crawled">
          {formatRelative(inspection.lastCrawlTime)}
          {inspection.crawledAs ? ` as ${inspection.crawledAs.toLowerCase()}` : ''}
        </Fact>
      )}

      {canonicalMismatch && (
        <Fact label="Canonical">
          <span className="text-warning">{inspection.googleCanonical}</span>
          <span className="text-muted-foreground"> chosen over the declared one</span>
        </Fact>
      )}

      {inspection.robotsTxtState && inspection.robotsTxtState !== 'ALLOWED' && (
        <Fact label="Robots">
          <span className="text-warning">{inspection.robotsTxtState}</span>
        </Fact>
      )}

      <Fact label="Sitemap">
        {inspection.sitemaps.length > 0
          ? `listed in ${inspection.sitemaps.length}`
          : 'not listed in one'}
      </Fact>
    </dl>
  )
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="mono-label text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate font-mono">{children}</dd>
    </>
  )
}
